/**
 * 图片状态管理
 * 管理图片列表、当前图片、上传图片、预览等
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { DEFAULT_ZOOM_SCALE, DEFAULT_IMAGE_PREFIX } from '../const/constants.js';

/**
 * @typedef {Object} ImageItem
 * @property {string} url - 图片 URL
 * @property {string} previewUrl - 预览 URL
 * @property {string} OriginUrl - 原始 URL
 * @property {string} type - 图片类型 'url' | 'base64'
 * @property {string} fileType - 文件类型
 * @property {string} prefix - 参数前缀
 * @property {boolean} isExample - 是否为示例图片
 * @property {boolean} cantPreview - 是否禁止预览
 * @property {Array} pdfImgList - PDF 页面图片列表
 * @property {Object} defaultConfig - 默认配置
 */

/**
 * @typedef {Object} RectItem
 * @property {string} key - 矩形框唯一标识
 * @property {string} label - 标签文本
 * @property {boolean} visible - 是否可见
 * @property {Object} position - 位置信息
 */

const initialState = {
  // 图片列表
  imgList: [],           // 示例图片列表
  uploadImgList: [],     // 上传图片列表
  imgListType: 'default', // 'default' | 'upload'
  
  // 当前图片
  currentImage: null,
  
  // 预览相关
  angle: 0,              // 旋转角度
  range: DEFAULT_ZOOM_SCALE, // 缩放比例
  imgPreType: 'preview', // 'preview' | 'col' | 'split'
  showImgList: true,     // 是否显示图片列表
  
  // 矩形框
  rects: [],             // 矩形框列表
  rectDocuments: [],     // 原始矩形框数据
  selectRectKey: '',     // 选中的矩形框 key
  
  // 裁剪
  clipImg: '',           // 裁剪后的图片 base64
  
  // 分页
  totalPage: 1,
  currentPage: 1,
  
  // 图片前缀
  prefix: DEFAULT_IMAGE_PREFIX,
  
  // 状态
  isImageError: false,
};

export const useImageStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // ========== 图片列表操作 ==========

      /**
       * 设置示例图片列表
       * @param {Array<ImageItem>} list
       */
      setImgList: (list) => set({ imgList: list }, false, 'image/setImgList'),

      /**
       * 设置上传图片列表
       * @param {Array<ImageItem>} list
       */
      setUploadImgList: (list) => set({ uploadImgList: list }, false, 'image/setUploadImgList'),

      /**
       * 添加上传图片
       * @param {ImageItem} img
       */
      addUploadImage: (img) => {
        const { uploadImgList } = get();
        const existingIndex = uploadImgList.findIndex(item => item.url === img.url);
        
        if (existingIndex !== -1) {
          // 已存在则更新
          const newList = [...uploadImgList];
          newList[existingIndex] = img;
          set({ uploadImgList: newList }, false, 'image/updateUploadImage');
        } else {
          // 不存在则添加到头部
          set({ uploadImgList: [img, ...uploadImgList] }, false, 'image/addUploadImage');
        }
      },

      /**
       * 删除上传图片
       * @param {number} index
       */
      removeUploadImage: (index) => {
        const { uploadImgList } = get();
        const newList = [...uploadImgList];
        newList.splice(index, 1);
        set({ uploadImgList: newList }, false, 'image/removeUploadImage');
      },

      /**
       * 清空上传图片列表
       */
      clearUploadImgList: () => set({ uploadImgList: [] }, false, 'image/clearUploadImgList'),

      /**
       * 设置图片列表类型
       * @param {'default' | 'upload'} type
       */
      setImgListType: (type) => set({ imgListType: type }, false, 'image/setImgListType'),

      // ========== 当前图片操作 ==========

      /**
       * 设置当前图片
       * @param {ImageItem} img
       */
      setCurrentImage: (img) => set({ currentImage: img }, false, 'image/setCurrentImage'),

      /**
       * 更新当前图片属性
       * @param {Partial<ImageItem>} updates
       */
      updateCurrentImage: (updates) => {
        const { currentImage } = get();
        if (currentImage) {
          set({ currentImage: { ...currentImage, ...updates } }, false, 'image/updateCurrentImage');
        }
      },

      // ========== 预览控制 ==========

      /**
       * 设置旋转角度
       * @param {number} angle
       */
      setAngle: (angle) => set({ angle }, false, 'image/setAngle'),

      /**
       * 设置缩放比例
       * @param {number} range
       */
      setRange: (range) => set({ range }, false, 'image/setRange'),

      /**
       * 设置预览类型
       * @param {'preview' | 'col' | 'split'} type
       */
      setImgPreType: (type) => set({ imgPreType: type }, false, 'image/setImgPreType'),

      /**
       * 切换图片列表显示
       * @param {boolean} show
       */
      setShowImgList: (show) => set({ showImgList: show }, false, 'image/setShowImgList'),

      // ========== 矩形框操作 ==========

      /**
       * 设置矩形框列表
       * @param {Array<RectItem>} rects
       */
      setRects: (rects) => set({ rects }, false, 'image/setRects'),

      /**
       * 设置原始矩形框数据（自动转换为可见矩形框）
       * @param {Array} documents
       */
      setRectDocuments: (documents) => {
        const rects = documents.map(r => ({ ...r, visible: true }));
        set({ rectDocuments: documents, rects }, false, 'image/setRectDocuments');
      },

      /**
       * 设置选中的矩形框
       * @param {string} key
       */
      setSelectRectKey: (key) => set({ selectRectKey: key }, false, 'image/setSelectRectKey'),

      /**
       * 切换矩形框可见性
       * @param {string} key
       */
      toggleRectVisible: (key) => {
        const { rects } = get();
        const newRects = rects.map(r => 
          r.key === key ? { ...r, visible: !r.visible } : r
        );
        set({ rects: newRects }, false, 'image/toggleRectVisible');
      },

      // ========== 裁剪操作 ==========

      /**
       * 设置裁剪图片
       * @param {string} clipImg
       */
      setClipImg: (clipImg) => set({ clipImg }, false, 'image/setClipImg'),

      // ========== 分页操作 ==========

      /**
       * 设置总页数
       * @param {number} total
       */
      setTotalPage: (total) => set({ totalPage: total }, false, 'image/setTotalPage'),

      /**
       * 设置当前页码
       * @param {number} page
       */
      setCurrentPage: (page) => set({ currentPage: page }, false, 'image/setCurrentPage'),

      // ========== 其他操作 ==========

      /**
       * 设置图片参数前缀
       * @param {string} prefix
       */
      setPrefix: (prefix) => set({ prefix }, false, 'image/setPrefix'),

      /**
       * 设置图片错误状态
       * @param {boolean} isError
       */
      setIsImageError: (isError) => set({ isImageError: isError }, false, 'image/setIsImageError'),

      /**
       * 清空图片相关状态（切换菜单时调用）
       */
      clearImageState: () => set({
        rects: [],
        rectDocuments: [],
        selectRectKey: '',
        clipImg: '',
        showImgList: true,
        angle: 0,
      }, false, 'image/clearImageState'),

      /**
       * 重置所有状态
       */
      resetImage: () => set(initialState, false, 'image/reset'),
    }),
    { name: 'ImageStore' }
  )
);
